import React, { useState, useEffect, useMemo } from 'react';
import { batchActions } from 'redux-batched-actions';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Avatar from '@atlaskit/avatar';
import { fg } from '@atlassian/jira-feature-gating';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIntlV2 } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { UNASSIGNED_ID } from '@atlassian/jira-issue-field-assignee/src/common/constants.tsx';
import type { UserOption, User } from '@atlassian/jira-issue-field-assignee/src/common/types.tsx';
import {
	getUnassignedUser,
	getAutomaticUserOption,
	getUserOptionFromUser,
} from '@atlassian/jira-issue-field-assignee/src/common/utils.tsx';
import { ASSIGNEE } from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	type MenuNode,
	type MenuItem,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useUserSubscriber } from '@atlassian/jira-user-services/src/main.tsx';
import { useContextStartAnalytics } from '../../../../../../common/hooks/use-context-start-analytics/index.tsx';
import type { Person } from '../../../../../../model/types.tsx';
import { cardAssigneeSetOptimistic } from '../../../../../../state/actions/assignee/index.tsx';
import { useBacklogActionCreator, useBacklogSelector } from '../../../../../../state/index.tsx';
import {
	makeGetCardAssignee,
	makeCardKeyFromCardId,
} from '../../../../../../state/selectors/cards/index.tsx';
import {
	canAssignIssues,
	canBulkEditIssues,
} from '../../../../../../state/selectors/permissions/index.tsx';
import { ASSIGNEE_MENU_ID, ASSIGNEE_SEARCH_MENU_ID } from '../../constants.tsx';
import messages from '../../messages.tsx';
import type { ContentProps } from './types.tsx';

export const useAssigneeContent = ({
	options,
	isLoading,
	canAssign,
	setQuery,
	selectedCardIds,
}: ContentProps): MenuNode[] => {
	const onMouseEnter = useContextStartAnalytics({
		selectedCardIds: selectedCardIds ?? [],
		fieldType: ASSIGNEE,
		fieldKey: ASSIGNEE,
	});
	const { formatMessage } = useIntl();

	return canAssign
		? [
				{
					type: MenuNodeType.Item,
					label: formatMessage(messages.assignee),
					id: ASSIGNEE_MENU_ID,
					submenuWidth: 284,
					children: [
						{
							type: MenuNodeType.Search,
							id: ASSIGNEE_SEARCH_MENU_ID,
							children: options,
							hasSearch: true,
							isAsyncSearch: true,
							isLoading,
							onChange: (value) => setQuery(value),
							ariaLabel: formatMessage(messages.ariaLabel),
						},
					],
					...(fg('one_event_rules_them_all_fg') && {
						onMouseEnter,
					}),
				},
			]
		: [];
};

export const useAssigneeEndAnalytics = () => {
	const getCurrentAssignee = useBacklogSelector(makeGetCardAssignee);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (cardId: number, newValue: Person | null) => {
		const previousValue = getCurrentAssignee(cardId);
		getUpdateAnalyticsFlowHelper().setActionTakenAttributes(
			ASSIGNEE,
			previousValue?.id,
			newValue?.id ?? null,
		);
		getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(ASSIGNEE, {
			analytics: createAnalyticsEvent({}),
			attributes: {
				fieldType: ASSIGNEE,
			},
		});
	};
};

export const useAssignee = ({ selectedCardIds }: { selectedCardIds: number[] }) => {
	const intl = useIntlV2(); // used to obtain unassigned and automatic user options
	const { formatMessage } = useIntl();
	const [assignees, setAssignees] = useState<UserOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [query, setQuery] = useState('');
	const getIssueKey = useBacklogSelector(makeCardKeyFromCardId);

	const [{ data: loggedInUser }] = useUserSubscriber();
	const unassigned = useMemo(() => getUserOptionFromUser(getUnassignedUser(intl)), [intl]);
	const automatic = useMemo(() => getUserOptionFromUser(getAutomaticUserOption(intl)), [intl]);
	const fireAnalyticsEnd = fg('one_event_rules_them_all_fg')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useAssigneeEndAnalytics()
		: noop;
	const assignToMe = useMemo(
		() =>
			loggedInUser
				? getUserOptionFromUser({
						...loggedInUser,
						displayName: formatMessage(messages.assignToMe),
					})
				: null,
		[formatMessage, loggedInUser],
	);

	const defaultOptions = useMemo(
		() => [unassigned, automatic, ...(assignToMe ? [assignToMe] : [])],
		[assignToMe, automatic, unassigned],
	);

	const onAssigneeClick = useBacklogActionCreator(
		(assignee: UserOption, analyticsEvent: UIAnalyticsEvent) => {
			if (selectedCardIds.length === 1 && fg('one_event_rules_them_all_fg')) {
				fireAnalyticsEnd(selectedCardIds[0], assignee.id === UNASSIGNED_ID ? null : assignee);
			}
			return batchActions(
				selectedCardIds.map((id) =>
					cardAssigneeSetOptimistic(
						{
							cardId: id,
							assignee:
								assignee.id === UNASSIGNED_ID
									? null
									: {
											avatarUrl: assignee.avatarUrl,
											displayName: assignee.name,
											id: assignee.id,
										},
						},
						getIssueKey(id),
						analyticsEvent,
					),
				),
			);
		},
	);

	const fetchUsers = (keywords: string): Promise<User[]> =>
		fetchJson(`/rest/api/2/user/search?query=${keywords}`);

	useEffect(() => {
		const handleSearch = async (value: string) => {
			setIsLoading(true);
			try {
				const response = await fetchUsers(value);
				setAssignees(response.length ? response.map(getUserOptionFromUser) : []);
			} catch {
				setAssignees(defaultOptions);
			}
			setIsLoading(false);
		};

		if (query.length) {
			handleSearch(query);
		} else {
			setAssignees(defaultOptions);
		}
	}, [query, defaultOptions]);

	const options: MenuItem[] = useMemo(
		() =>
			assignees.map((assignee) => ({
				type: MenuNodeType.Item,
				label: assignee.name,
				id: `${ASSIGNEE_MENU_ID}__value_${assignee.id}`,
				icon: <Avatar src={assignee.avatarUrl} size="small" />,
				onClick: (analyticsEvent) => onAssigneeClick(assignee, analyticsEvent),
			})),
		[assignees, onAssigneeClick],
	);

	const canBulkEdit = useBacklogSelector(canBulkEditIssues);
	const canAssignIssue = useBacklogSelector(canAssignIssues);
	const canAssign = canAssignIssue && canBulkEdit;

	return useAssigneeContent({
		options,
		isLoading,
		canAssign,
		setQuery,
		...(fg('one_event_rules_them_all_fg') && { selectedCardIds }),
	});
};
